import type { Subscription } from '../../app/models/User';

export type PlanTier = 'core' | 'pro' | 'max';

// Display metadata only. Never use this module to authorize requests or disable controls.
// Unqualified Pro is the existing $229 subscription, now presented as PartFits Max.
export const getPresentedPlanTier = (name?: string): PlanTier | undefined => {
    switch (name?.trim().toLowerCase()) {
        case 'standard': case 'partfits core': return 'core';
        case 'growth': case 'partfits pro': return 'pro';
        case 'pro': case 'partfits max': return 'max';
        default: return undefined;
    }
};

export const PLAN_FEATURES = {
    themeResults: { tier: 'core', label: 'Shopify theme results' },
    shopping: { tier: 'core', label: 'Shopping features' },
    selectorFields: { tier: 'core', label: 'Selector fields' },
    spreadsheetImports: { tier: 'core', label: 'CSV/XLSX fitment imports' },
    tagImports: { tier: 'core', label: 'Product-tag imports' },
    collectionImports: { tier: 'core', label: 'Vehicle-to-collection imports' },
    exports: { tier: 'core', label: 'CSV fitment export' },
    turn14: { tier: 'core', label: 'Turn14 fitment sync' },
    basicReports: { tier: 'core', label: 'Search reports' },
    partfitsResults: { tier: 'pro', label: 'PartFits-built product results' },
    resultGrouping: { tier: 'pro', label: 'Grouping by collection or product type' },
    resultFilters: { tier: 'pro', label: 'PartFits result filters' },
    coverageReports: { tier: 'pro', label: 'Fitment coverage reports' },
    demandComparison: { tier: 'pro', label: 'Search demand comparison' },
    klaviyo: { tier: 'pro', label: 'Klaviyo vehicle syncing' },
    customGroups: { tier: 'max', label: 'Custom result groups' },
    vcdb: { tier: 'max', label: 'Independent VCDB reference access' },
    aces: { tier: 'max', label: 'ACES XML imports' },
} as const;

export type PlanFeature = keyof typeof PLAN_FEATURES;
export type PlanFeatureState = 'included' | 'upgrade' | 'legacy' | 'retained' | 'preview';
const rank: Record<PlanTier, number> = { core: 0, pro: 1, max: 2 };

export const getPlanFeaturePresentation = (feature: PlanFeature, subscription?: Subscription, legacy = false) => {
    const requiredTier = PLAN_FEATURES[feature].tier;
    const tierLabel = requiredTier === 'core' ? 'All plans' : requiredTier === 'pro' ? 'PartFits Pro & Max' : 'PartFits Max';
    const currentTier = subscription?.active ? getPresentedPlanTier(subscription.name) : undefined;
    const state: PlanFeatureState = requiredTier === 'core' ? 'included'
        : legacy ? 'legacy'
        : currentTier ? rank[currentTier] >= rank[requiredTier] ? 'included' : 'upgrade'
        : subscription?.active ? 'retained' : 'preview';
    const status = {
        included: 'Included in your plan',
        upgrade: 'Upgrade to unlock',
        legacy: 'Included through legacy access',
        retained: 'Current access retained',
        preview: 'Plan preview',
    }[state];
    return { tierLabel, state, status, requiredTier };
};
